/**
 * Finance API Layer.
 *
 * React Query hooks for credit accounts and transactions.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";
import type {
  CreditAccount,
  CreditAccountCreate,
  CreditTransaction,
} from "@/types/credit-account";

export function useCreditAccounts(page = 1, pageSize = 25) {
  return useQuery<PaginatedResponse<CreditAccount>>({
    queryKey: ["credit-accounts", { page, pageSize }],
    queryFn: async () => {
      const resp = await apiClient.get(
        `/api/v1/credit-accounts?page=${page}&page_size=${pageSize}`,
      );
      return resp.data as PaginatedResponse<CreditAccount>;
    },
  });
}

export function useCreditAccount(accountId: string | undefined) {
  return useQuery<CreditAccount>({
    queryKey: ["credit-accounts", accountId],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/credit-accounts/${accountId}`);
      return resp.data as CreditAccount;
    },
    enabled: !!accountId,
  });
}

export function useCreateCreditAccount() {
  const qc = useQueryClient();
  return useMutation<CreditAccount, Error, CreditAccountCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post("/api/v1/credit-accounts", data);
      return resp.data as CreditAccount;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["credit-accounts"] }),
  });
}

export function useCreditTransactions(accountId: string | undefined) {
  return useQuery<CreditTransaction[]>({
    queryKey: ["credit-accounts", accountId, "transactions"],
    queryFn: async () => {
      const resp = await apiClient.get(
        `/api/v1/credit-accounts/${accountId}/transactions`,
      );
      return resp.data as CreditTransaction[];
    },
    enabled: !!accountId,
  });
}

export function useRecordPayment(accountId: string) {
  const qc = useQueryClient();
  return useMutation<
    CreditTransaction,
    Error,
    { amount: number; description?: string }
  >({
    mutationFn: async (data) => {
      const resp = await apiClient.post(
        `/api/v1/credit-accounts/${accountId}/payment`,
        data,
      );
      return resp.data as CreditTransaction;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credit-accounts", accountId] });
      qc.invalidateQueries({
        queryKey: ["credit-accounts", accountId, "transactions"],
      });
    },
  });
}
